import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/axios"
import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useState } from "react"
import { FaPlus } from "react-icons/fa6"
import { Loader2 } from "lucide-react"
import { KonsentrasiProdi, Unitkerja } from "@/types"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import toast from "react-hot-toast"
import { AxiosError } from "axios"
import Combobox from "@/components/Combobox"
import { getListUnitkerja } from "@/services/getData"

const postData = async (newData: KonsentrasiProdi) => {
  const response = await api.post("/master/konsentrasi-prodi/store", newData)
  return response.data
}

const patchData = async (newData: KonsentrasiProdi) => {
  const response = await api.patch("/master/konsentrasi-prodi/update", newData)
  return response.data
}

const deleteData = async (deletedData: {
  id_ref_konsentrasi_prodi: string
}) => {
  const response = await api({
    url: "/master/konsentrasi-prodi/delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: KonsentrasiProdi | null
  closeModal: () => void
}) {
  const { data: listUnitkerja } = useQuery({
    queryKey: ["listUnitkerja"],
    queryFn: getListUnitkerja,
    select: (data) =>
      data?.map((unitkerja: Unitkerja) => ({
        label: unitkerja.nama,
        value: `${unitkerja.uk_id}`,
        unitkerjaGroupKey: unitkerja.unitkerja_group_key,
        isActive: unitkerja.is_active,
      })),
  })

  const optionsProdi = listUnitkerja?.filter(
    (option: {
      label: string
      value: string
      unitkerjaGroupKey: string
      isActive: boolean
    }) => {
      return option?.unitkerjaGroupKey == "5" && option.isActive
    }
  )

  const [openListProdi, setOpenListProdi] = useState(false)
  const [form, setForm] = useState<KonsentrasiProdi>({
    id_ref_konsentrasi_prodi: selectedRow?.id_ref_konsentrasi_prodi || "",
    nama_konsentrasi: selectedRow?.nama_konsentrasi || "",
    uk_id: selectedRow?.uk_id || "",
  })

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listKonsentrasiProdi"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Konsentrasi Prodi"
          description={`Apakah anda yakin akan menghapus data jalur "${selectedRow?.nama_konsentrasi}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={
          isPost
            ? "Insert Data Konsentrasi Prodi"
            : "Update Data Konsentrasi Prodi"
        }
      />

      <div>
        <Label>Prodi</Label>
        <Combobox
          open={openListProdi}
          setOpen={setOpenListProdi}
          value={form.uk_id || ""}
          setValue={(value) => {
            setForm((prev) => ({ ...prev, uk_id: value }))
          }}
          options={optionsProdi}
          placeholder="Pilih prodi"
        />
      </div>

      <div>
        <Label>Nama Konsentrasi Prodi</Label>
        <Input
          placeholder="Ketik nama konsentrasi prodi..."
          value={form.nama_konsentrasi}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              nama_konsentrasi: event.target.value,
            }))
          }
        />
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
